import { useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import { Upload, Crop, Trash2, ArrowUp, ArrowDown, Loader2, ImagePlus, X, Eye, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { api, errMsg, fileUrl, uploadFile, useFetch } from "@/lib/api";
import { cropToFile, optimizeOriginal, loadImage } from "@/lib/crop";

const ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif";

const CropDialog = ({ job, onClose, onSaved }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const cropped = await cropToFile(job.src, area);
      const photo_url = await uploadFile(cropped);
      let photo_original_url = job.member.photo_original_url || "";
      if (job.isNew) photo_original_url = await uploadFile(await optimizeOriginal(job.src));
      await api.put(`/manage/board/${job.member.id}`, { photo_url, photo_original_url });
      toast.success(`Photo saved for ${job.member.name}`);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg bg-ink border-line p-4 sm:p-6" data-testid="board-crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black uppercase italic">Crop • {job.member.name}</DialogTitle>
          <DialogDescription className="text-slate-400">Drag to reposition. Pinch or use the slider to zoom.</DialogDescription>
        </DialogHeader>
        <div className="relative w-full aspect-[4/5] max-h-[55svh] mx-auto bg-pitch">
          <Cropper image={job.src} crop={crop} zoom={zoom} aspect={4 / 5} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={(_, px) => setArea(px)} objectFit="contain" />
        </div>
        <div className="flex items-center gap-3 px-1">
          <span className="font-mono text-[10px] uppercase text-slate-400">Zoom</span>
          <Slider value={[zoom]} min={1} max={4} step={0.01} onValueChange={([z]) => setZoom(z)} data-testid="board-crop-zoom" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <button onClick={onClose} disabled={busy} className="btn-line !py-3 !text-base" data-testid="board-crop-cancel">Cancel</button>
          <button onClick={save} disabled={busy || !area} className="btn-gold !py-3 !text-base" data-testid="board-crop-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save Photo</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const matchMember = (file, members) => {
  const n = file.name.toLowerCase().replace(/[^a-z]/g, "");
  return members.find((m) => n.includes(m.name.toLowerCase().replace(/coach/g, "").replace(/[^a-z]/g, "")))?.id || "";
};

const Queue = ({ queue, members, setQueue, startCrop }) => (
  <div className="card-navy p-4 space-y-3" data-testid="board-upload-queue">
    <p className="font-display text-xl font-black uppercase italic">Uploaded — assign & crop</p>
    {queue.map((q) => (
      <div key={q.key} className="flex items-center gap-3 border-b border-line/60 pb-3 last:border-0" data-testid={`board-queue-item-${q.key}`}>
        <img src={q.src} alt="" className="h-16 w-14 object-cover bg-pitch shrink-0" />
        <select className="field !py-2.5 min-w-0" value={q.memberId} onChange={(e) => setQueue(queue.map((x) => (x.key === q.key ? { ...x, memberId: e.target.value } : x)))} data-testid={`board-queue-member-${q.key}`}>
          <option value="">Choose board member…</option>
          {members.map((m) => <option key={m.id} value={m.id}>{m.name} — {m.title}</option>)}
        </select>
        <button disabled={!q.memberId} onClick={() => startCrop(q)} className="btn-gold !px-4 !py-2.5 !text-sm shrink-0" data-testid={`board-queue-crop-${q.key}`}><Crop className="h-4 w-4" /> Crop</button>
        <button onClick={() => setQueue(queue.filter((x) => x.key !== q.key))} className="p-2 text-slate-400 hover:text-red-400" aria-label="Remove from queue"><X className="h-5 w-5" /></button>
      </div>
    ))}
  </div>
);

const MemberTile = ({ m, i, total, onReplace, onAdjust, onRemove, onMove, onToggle }) => (
  <div className="card-navy overflow-hidden" data-testid={`board-photo-member-${m.id}`}>
    <div className="relative aspect-[4/5] bg-pitch">
      {m.photo_url ? <img src={fileUrl(m.photo_url)} alt={m.name} className="h-full w-full object-cover" data-testid={`board-photo-img-${m.id}`} /> : <div className="absolute inset-0 stripes flex items-center justify-center"><ImagePlus className="h-10 w-10 text-line" /></div>}
      <span className="absolute left-2 top-2 bg-gold px-2 py-0.5 font-display font-black text-navy">{String(i + 1).padStart(2, "0")}</span>
      {!m.published && <span className="absolute right-2 top-2 bg-pitch/90 px-2 py-0.5 font-mono text-[10px] uppercase">Hidden</span>}
    </div>
    <div className="p-3">
      <p className="font-display text-lg font-black uppercase italic leading-tight">{m.name}</p>
      <p className="text-xs text-slate-400 truncate">{m.title}</p>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button onClick={() => onReplace(m)} className="btn-gold !px-2 !py-2.5 !text-sm" data-testid={`board-photo-upload-${m.id}`}><Upload className="h-4 w-4" /> {m.photo_url ? "Replace" : "Upload"}</button>
        <button onClick={() => onAdjust(m)} disabled={!m.photo_original_url && !m.photo_url} className="btn-line !px-2 !py-2 !text-sm" data-testid={`board-photo-adjust-${m.id}`}><Crop className="h-4 w-4" /> Crop</button>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <div className="flex">
          <button disabled={i === 0} onClick={() => onMove(i, -1)} className="p-2.5 text-slate-300 disabled:opacity-30 hover:text-gold" aria-label="Move up" data-testid={`board-move-up-${m.id}`}><ArrowUp className="h-5 w-5" /></button>
          <button disabled={i === total - 1} onClick={() => onMove(i, 1)} className="p-2.5 text-slate-300 disabled:opacity-30 hover:text-gold" aria-label="Move down" data-testid={`board-move-down-${m.id}`}><ArrowDown className="h-5 w-5" /></button>
          <button onClick={() => onToggle(m)} className="p-2.5 text-slate-300 hover:text-gold" aria-label={m.published ? "Hide profile" : "Publish profile"} data-testid={`board-toggle-${m.id}`}>{m.published ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}</button>
        </div>
        {m.photo_url && <button onClick={() => onRemove(m)} className="p-2.5 text-slate-300 hover:text-red-400" aria-label="Remove photo" data-testid={`board-photo-remove-${m.id}`}><Trash2 className="h-5 w-5" /></button>}
      </div>
    </div>
  </div>
);

export const BoardPhotoManager = () => {
  const { data, loading, reload } = useFetch("/manage/board");
  const members = data || [];
  const [queue, setQueue] = useState([]);
  const [job, setJob] = useState(null);
  const multiRef = useRef(null);
  const singleRef = useRef(null);
  const target = useRef(null);

  const readable = async (file) => {
    const src = URL.createObjectURL(file);
    try {
      await loadImage(src);
      return src;
    } catch (e) {
      toast.error(`${file.name}: this browser can't open HEIC. On iPhone choose the photo from Photos (it converts automatically), or save it as JPG.`);
      return null;
    }
  };
  const onMulti = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const items = [];
    for (const f of files) {
      const src = await readable(f);
      if (src) items.push({ key: `${Date.now()}-${items.length}-${f.name}`, src, memberId: matchMember(f, members) });
    }
    setQueue((q) => [...q, ...items]);
  };
  const onSingle = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !target.current) return;
    const src = await readable(f);
    if (src) setJob({ src, member: target.current, isNew: true });
  };
  const replace = (m) => {
    target.current = m;
    singleRef.current?.click();
  };
  const adjust = (m) => setJob({ src: fileUrl(m.photo_original_url || m.photo_url), member: m, isNew: !m.photo_original_url });
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name}'s photo?`)) return;
    await api.put(`/manage/board/${m.id}`, { photo_url: "", photo_original_url: "" });
    toast.success("Photo removed");
    reload();
  };
  const move = async (i, dir) => {
    const ids = members.map((m) => m.id);
    [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]];
    await api.post("/admin/board/reorder", { ids });
    reload();
  };
  const toggle = async (m) => {
    await api.put(`/manage/board/${m.id}`, { published: !m.published });
    reload();
  };
  const startCrop = (q) => setJob({ src: q.src, member: members.find((m) => m.id === q.memberId), isNew: true, queueKey: q.key });
  const saved = () => {
    if (job?.queueKey) setQueue((q) => q.filter((x) => x.key !== job.queueKey));
    setJob(null);
    reload();
  };

  return (
    <div className="space-y-6" data-testid="board-photo-manager">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-3xl font-black uppercase italic">Board Photo Manager</h2>
          <p className="text-sm text-slate-400 max-w-xl">Upload one or many photos from your iPhone or computer, assign each to a board member, crop and save. Photos are stored permanently and appear on Meet the Board instantly.</p>
        </div>
        <button onClick={() => multiRef.current?.click()} className="btn-gold shrink-0" data-testid="board-upload-multiple"><ImagePlus className="h-5 w-5" /> Upload Photos</button>
      </div>
      <input ref={multiRef} type="file" accept={ACCEPT} multiple className="hidden" onChange={onMulti} data-testid="board-upload-multiple-input" />
      <input ref={singleRef} type="file" accept={ACCEPT} className="hidden" onChange={onSingle} data-testid="board-upload-single-input" />
      {queue.length > 0 && <Queue queue={queue} members={members} setQueue={setQueue} startCrop={startCrop} />}
      {loading ? <p className="text-slate-400">Loading…</p> : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
          {members.map((m, i) => <MemberTile key={m.id} m={m} i={i} total={members.length} onReplace={replace} onAdjust={adjust} onRemove={remove} onMove={move} onToggle={toggle} />)}
        </div>
      )}
      {job?.member && <CropDialog job={job} onClose={() => setJob(null)} onSaved={saved} />}
    </div>
  );
};
